/**
 * AnulacionNCPreviewModal
 *
 * Modal de vista previa antes de emitir una Nota de Crédito que anula una FE.
 * Razón: emitir una NC "a ciegas" produjo errores históricos (ej. Fiberlink NC 365
 * que no incluyó Cruz Roja + 911). Este modal muestra la FE original (izquierda) y
 * la NC propuesta (derecha) lado a lado, con validación visual de totales.
 *
 * Reglas:
 *   - Solo lectura. Si la NC propuesta no cuadra con la FE → no se permite enviar.
 *   - Si todo cuadra → se puede guardar borrador o enviar directo a Hacienda.
 *
 * Props:
 *   - selectedDoc: { id, consecutivo, ... } — FE seleccionada en HistorialFacturas
 *   - razonInicial: string (default "Anulación total del documento")
 *   - motivo: 'anulacion' | 'descuento' | 'devolucion' (define el referencia.codigo)
 *   - onClose: () => void
 *   - onCreated: (result) => void  — callback tras emitir la NC con éxito
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, FileText, Loader2, X, Send, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { documentsApi } from '../utils/api';

const fmt = (n, mon = 'USD') => {
  const v = parseFloat(n || 0);
  return (mon === 'USD' ? '$' : '₡') + v.toLocaleString(mon === 'USD' ? 'en-US' : 'es-CR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Diferencia tolerada (centavos de redondeo)
const TOLERANCIA = 0.01;

export default function AnulacionNCPreviewModal({ selectedDoc, razonInicial, motivo = 'anulacion', onClose, onCreated }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [docData, setDocData] = useState(null);
  const [razon, setRazon] = useState(razonInicial || 'Anulación total del documento');

  // Cargar datos completos de la FE (con líneas)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await documentsApi.getById(selectedDoc.id);
        if (cancelled) return;
        const d = res.data?.data;
        if (!d) throw new Error('No se pudo cargar la FE');
        setDocData(d);
      } catch (err) {
        if (cancelled) return;
        toast.error('Error al cargar FE: ' + (err.response?.data?.error || err.message));
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDoc.id, onClose]);

  // Construir el payload de la NC (espejado de handleConfirmNC original)
  const ncPayload = useMemo(() => {
    if (!docData) return null;
    const tipoCambioOriginal = parseFloat(docData.tipoCambio) || parseFloat(docData.tipo_cambio) || 1;
    // condicionVenta='02' (crédito) exige plazoCredito (string) o Hacienda
    // rechaza con -58 "Plazo del crédito no posee la estructura establecida".
    // Copiamos plazo de la FE original; default 30 si la FE lo trae vacío.
    const condVenta = docData.condicionVenta || '01';
    const plazoCred = condVenta === '02'
      ? String(docData.plazoCredito || docData.plazo_credito || 30)
      : null;
    return {
      tipoDocumento: '03',
      receptorId: docData.receptorId || null,
      condicionVenta: condVenta,
      plazoCredito: plazoCred,
      medioPago: docData.medioPago || ['01'],
      codigoMoneda: docData.codigoMoneda || 'USD',
      tipoCambio: tipoCambioOriginal,
      notas: razon,
      referencia: {
        tipoDoc: docData.tipoDocumento || '01',
        numero: docData.clave,
        consecutivo: docData.consecutivo,
        fecha: docData.fechaEmision ? docData.fechaEmision.split('T')[0] : new Date().toISOString().split('T')[0],
        codigo: motivo === 'anulacion' ? '01' : motivo === 'descuento' ? '02' : '03',
        razon
      },
      lineas: motivo === 'anulacion' ? (docData.lineas || []).map((l, idx) => {
        const lineaNC = {
          numeroLinea: idx + 1,
          cantidad: l.cantidad || 1,
          detalle: l.detalle,
          codigo: l.codigo || undefined,
          codigoComercial: l.sku || l.codigoComercial || undefined,
          precioUnitario: l.precioUnitario || 0,
          descuento: l.descuentoMonto || 0,
          descuentoPorcentaje: l.descuentoPorcentaje || 0,
          impuesto: {
            codigo: '01',
            codigoTarifa: l.impuestoCodigoTarifa || '08',
            tarifa: l.impuestoTarifa || 13
          },
          impuestosAdicionales: l.impuestosAdicionales || l.impuestos_adicionales || []
        };
        const exoneracion = l.impuestoExoneracion || l.impuesto_exoneracion;
        if (exoneracion) {
          lineaNC.impuesto.exoneracion = {
            tipoDocumento: exoneracion.tipoDocumento || exoneracion.TipoDocumentoEX1 || '04',
            numeroDocumento: exoneracion.numeroDocumento || exoneracion.NumeroDocumento,
            nombreInstitucion: exoneracion.nombreInstitucion || exoneracion.NombreInstitucion,
            fechaEmision: exoneracion.fechaEmision || exoneracion.FechaEmisionEX,
            porcentaje: exoneracion.porcentaje || exoneracion.TarifaExonerada || exoneracion.PorcentajeExoneracion,
            monto: exoneracion.monto || exoneracion.MontoExoneracion
          };
        }
        return lineaNC;
      }) : []
    };
  }, [docData, motivo, razon]);

  // Calcular totales de la NC propuesta (para comparar con la FE)
  const totales = useMemo(() => {
    if (!ncPayload || !docData) return null;

    // Los totales del backend vienen en docData.totales.X (camelCase, ya parseados).
    // Fallback a flat snake_case por si algún endpoint legacy responde diferente.
    const t = docData.totales || {};
    const feTotal = parseFloat(t.totalComprobante ?? docData.total_comprobante ?? 0);
    const feSubtotal = parseFloat(t.totalVentaNeta ?? docData.total_venta_neta ?? 0);
    const feIva = parseFloat(t.totalImpuesto ?? docData.total_impuesto ?? 0);
    const feOtros = parseFloat(t.totalOtrosCargos ?? docData.total_otros_cargos ?? 0);

    let ncSubtotal = 0;
    let ncIva = 0;
    let ncOtros = 0;
    for (const linea of ncPayload.lineas) {
      const cant = parseFloat(linea.cantidad) || 0;
      const precio = parseFloat(linea.precioUnitario) || 0;
      const desc = parseFloat(linea.descuento) || 0;
      const neto = cant * precio - desc;
      ncSubtotal += neto;
      const tarifa = parseFloat(linea.impuesto?.tarifa) || 0;
      let ivaLinea = neto * tarifa / 100;
      if (linea.impuesto?.exoneracion?.porcentaje) {
        const exo = neto * parseFloat(linea.impuesto.exoneracion.porcentaje) / 100;
        ivaLinea -= exo;
      }
      ncIva += Math.max(0, ivaLinea);
      if (linea.impuestosAdicionales?.length) {
        for (const impAd of linea.impuestosAdicionales) {
          if (impAd.monto !== undefined) {
            ncOtros += parseFloat(impAd.monto) || 0;
          } else if (impAd.porcentaje !== undefined) {
            ncOtros += neto * parseFloat(impAd.porcentaje) / 100;
          }
        }
      }
    }
    ncSubtotal = Math.round(ncSubtotal * 100) / 100;
    ncIva = Math.round(ncIva * 100) / 100;
    ncOtros = Math.round(ncOtros * 100) / 100;
    const ncTotal = Math.round((ncSubtotal + ncIva + ncOtros) * 100) / 100;

    const dif = Math.round((feTotal - ncTotal) * 100) / 100;
    const ok = Math.abs(dif) <= TOLERANCIA;

    return { feTotal, feSubtotal, feIva, feOtros, ncSubtotal, ncIva, ncOtros, ncTotal, dif, ok };
  }, [ncPayload, docData]);

  const handleEnviar = async (enviarHacienda) => {
    if (!totales?.ok) {
      toast.error('Los totales no cuadran — corregí antes de enviar');
      return;
    }
    setSending(true);
    try {
      const res = await documentsApi.create(ncPayload, enviarHacienda);
      toast.success(enviarHacienda
        ? 'Nota de Crédito enviada a Hacienda'
        : 'Nota de Crédito guardada como borrador'
      );
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear NC');
    } finally {
      setSending(false);
    }
  };

  const moneda = docData?.codigoMoneda || docData?.codigo_moneda || 'USD';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b bg-gradient-to-r from-red-50 to-rose-50 rounded-t-2xl shrink-0">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Vista Previa — Anular FE con Nota de Crédito</h2>
              <p className="text-xs text-gray-600 mt-0.5">
                Comprobá que la NC propuesta (derecha) cubra exactamente todos los conceptos de la FE original (izquierda) antes de enviar a Hacienda.
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={sending} className="text-gray-400 hover:text-gray-700 disabled:opacity-40">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            <span className="ml-2 text-sm text-gray-500">Cargando FE…</span>
          </div>
        ) : (
          <>
            {/* Razón */}
            <div className="px-6 py-3 border-b shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">Razón:</label>
              <input
                type="text"
                value={razon}
                onChange={(e) => setRazon(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-200 focus:border-red-400"
                placeholder="Describa el motivo..."
              />
            </div>

            {/* Aviso: la FE ya tiene un pago aplicado */}
            {(() => {
              const pagado = parseFloat(docData?.totalPagado ?? docData?.total_pagado) || 0;
              if (pagado <= 0.01) return null;
              return (
                <div className="mx-6 mt-3 bg-amber-50 border-l-4 border-amber-500 px-4 py-2.5 rounded-r flex items-start gap-2 shrink-0">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <strong>Esta FE ya tiene un pago aplicado por {fmt(pagado, moneda)}.</strong>
                    <div className="text-xs text-amber-800 mt-0.5">
                      Al anularla con la NC deberás también revertir el pago en CxC (Histórico de Pagos) para dejar la cartera consistente.
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Banner de validación de totales */}
            {totales && (
              totales.ok ? (
                <div className="mx-6 my-3 bg-emerald-50 border-l-4 border-emerald-500 px-4 py-2 rounded-r flex items-center gap-2 shrink-0">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm text-emerald-900">
                    <strong>Totales cuadran:</strong> {fmt(totales.feTotal, moneda)}. Listo para enviar.
                  </span>
                </div>
              ) : (
                <div className="mx-6 my-3 bg-red-50 border-l-4 border-red-500 px-4 py-2 rounded-r shrink-0">
                  <div className="flex items-center gap-2 text-sm text-red-900">
                    <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                    <div>
                      <strong>Los totales NO coinciden — no se puede enviar.</strong>
                      <div className="text-xs mt-0.5">
                        FE: <strong>{fmt(totales.feTotal, moneda)}</strong>
                        {' · '}NC propuesta: <strong>{fmt(totales.ncTotal, moneda)}</strong>
                        {' · '}Diferencia: <strong>{fmt(totales.dif, moneda)}</strong>
                      </div>
                      <div className="text-xs text-red-700 mt-0.5">
                        Cancelá y reportá esto. Probablemente la FE tiene cargos (otros cargos, descuentos) no replicados por el generador de NC.
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* 2 columnas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x flex-1 overflow-hidden min-h-0">

              {/* Izquierda: FE original */}
              <div className="flex flex-col min-h-0 overflow-hidden">
                <div className="px-4 py-2 bg-blue-50 border-b shrink-0 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <h3 className="font-semibold text-sm text-blue-900">FE Original</h3>
                  <span className="text-xs text-blue-700 font-mono ml-auto">{docData?.consecutivo}</span>
                </div>
                <div className="flex-1 overflow-auto p-3">
                  <ResumenLineas lineas={docData?.lineas || []} moneda={moneda} />
                  <Totales
                    subtotal={totales?.feSubtotal}
                    iva={totales?.feIva}
                    otros={totales?.feOtros}
                    total={totales?.feTotal}
                    moneda={moneda}
                    color="blue"
                  />
                </div>
              </div>

              {/* Derecha: NC propuesta */}
              <div className="flex flex-col min-h-0 overflow-hidden">
                <div className="px-4 py-2 bg-red-50 border-b shrink-0 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-red-600" />
                  <h3 className="font-semibold text-sm text-red-900">NC Propuesta</h3>
                  <span className="text-xs text-red-700 ml-auto">(borrador, no emitida)</span>
                </div>
                <div className="flex-1 overflow-auto p-3 bg-gray-50">
                  <ResumenLineas
                    lineas={(ncPayload?.lineas || []).map(l => ({
                      cantidad: l.cantidad,
                      detalle: l.detalle,
                      precioUnitario: l.precioUnitario,
                      impuestoMonto: (parseFloat(l.cantidad) * parseFloat(l.precioUnitario) - parseFloat(l.descuento || 0)) * (parseFloat(l.impuesto?.tarifa || 0) / 100),
                      impuestoTarifa: l.impuesto?.tarifa,
                      impuestosAdicionales: l.impuestosAdicionales
                    }))}
                    moneda={moneda}
                  />
                  <Totales
                    subtotal={totales?.ncSubtotal}
                    iva={totales?.ncIva}
                    otros={totales?.ncOtros}
                    total={totales?.ncTotal}
                    moneda={moneda}
                    color={totales?.ok ? 'emerald' : 'red'}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 rounded-b-2xl shrink-0 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 text-sm font-medium flex items-center gap-2"
          >
            <X className="h-4 w-4" /> Cancelar
          </button>
          <button
            onClick={() => handleEnviar(false)}
            disabled={sending || loading || !totales?.ok}
            className="px-4 py-2 border border-emerald-500 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar Borrador
          </button>
          <button
            onClick={() => handleEnviar(true)}
            disabled={sending || loading || !totales?.ok}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold flex items-center gap-2"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Enviando…' : 'Enviar a Hacienda'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Sub-componentes
// ───────────────────────────────────────────────────────────────────────

function ResumenLineas({ lineas, moneda }) {
  if (!lineas || lineas.length === 0) {
    return <p className="text-sm text-gray-400 italic">Sin líneas</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead className="bg-white border-b sticky top-0">
        <tr className="text-left text-gray-500 uppercase tracking-wide">
          <th className="py-1.5 px-1 font-medium">Detalle</th>
          <th className="py-1.5 px-1 font-medium text-right w-12">Cant</th>
          <th className="py-1.5 px-1 font-medium text-right w-20">Precio</th>
          <th className="py-1.5 px-1 font-medium text-right w-16">IVA</th>
          <th className="py-1.5 px-1 font-medium text-right w-20">Total</th>
        </tr>
      </thead>
      <tbody>
        {lineas.map((l, idx) => {
          const cant = parseFloat(l.cantidad) || 0;
          const precio = parseFloat(l.precioUnitario || l.precio_unitario) || 0;
          const subtotal = cant * precio;
          const iva = parseFloat(l.impuestoMonto || l.impuesto_monto) || 0;
          const tarifa = parseFloat(l.impuestoTarifa || l.impuesto_tarifa) || 0;
          const total = subtotal + iva;
          const impAd = l.impuestosAdicionales || l.impuestos_adicionales;
          const tieneAdicionales = impAd && (Array.isArray(impAd) ? impAd.length > 0 : impAd);
          return (
            <tr key={idx} className="border-b border-gray-100 align-top">
              <td className="py-1.5 px-1">
                <div className="text-gray-800 line-clamp-2">{l.detalle}</div>
                {tieneAdicionales && (
                  <div className="text-[10px] text-amber-700 mt-0.5">
                    + parafiscales (Cruz Roja / 911)
                  </div>
                )}
              </td>
              <td className="py-1.5 px-1 text-right font-mono">{cant}</td>
              <td className="py-1.5 px-1 text-right font-mono">{fmt(precio, moneda)}</td>
              <td className="py-1.5 px-1 text-right font-mono text-gray-500">
                {tarifa ? `${tarifa}%` : '—'}
              </td>
              <td className="py-1.5 px-1 text-right font-mono font-medium">{fmt(total, moneda)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Totales({ subtotal, iva, otros, total, moneda, color = 'gray' }) {
  const colorMap = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    gray: 'bg-gray-50 border-gray-200 text-gray-900'
  };
  return (
    <div className={`mt-3 rounded-lg border ${colorMap[color]} px-3 py-2 text-xs`}>
      <div className="flex justify-between py-0.5">
        <span>Subtotal:</span>
        <span className="font-mono">{fmt(subtotal, moneda)}</span>
      </div>
      <div className="flex justify-between py-0.5">
        <span>IVA:</span>
        <span className="font-mono">{fmt(iva, moneda)}</span>
      </div>
      {(otros > 0) && (
        <div className="flex justify-between py-0.5">
          <span>Otros cargos (Cruz Roja + 911):</span>
          <span className="font-mono">{fmt(otros, moneda)}</span>
        </div>
      )}
      <div className="flex justify-between py-1 border-t mt-1 pt-1 font-bold text-sm">
        <span>TOTAL:</span>
        <span className="font-mono">{fmt(total, moneda)}</span>
      </div>
    </div>
  );
}
